import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Box, Typography, Card, Chip, IconButton, CardContent, Grid, Paper, Container, Tooltip as TooltipCustom, useMediaQuery, Backdrop, CircularProgress, useTheme, Collapse, Stack } from '@mui/material';
import { Menu as MenuIcon, Help as HelpIcon, } from '@mui/icons-material';
// app UI controller for toggling sidenav
import { useMaterialUIController, setMiniSidenav } from "context";
import KpiMetricscards from "components/KPI/KpiMetricscards";
import TimeSeriescards from "components/TimeSeries/TimeSeriescards";
import HighlightsCards from "components/Highlights/Highlightscard";
import PieLegendCard from "components/Visualizations/Piecharts"
import Barcharts from "components/Visualizations/Barcharts"
import LineChartSubBrand from "components/Visualizations/Linechartsubbrand";
import LineChartsale from "components/Visualizations/LineChartsale"
import InventoryCharts from "components/Visualizations/Inventorycharts";
import WaterfallChart from "components/Visualizations/waterfallchart";
import FilterChip from "components/FilterChip/FilterChip";
import { fetchMyDashboard } from "services/mydashboard.service";
import { getTimeSeriesLabels } from "../../../components/TimeSeries/monthUtils";
import { updateSelectedChipNameHighlights } from "../../../components/FilterChip/chipPeriodStore";
import { useFilterContext } from "../../../../src/context/FilterContext";
import { fetchMyDashboardFilter } from "../../../services/mydashboardFilter.service";

const Dashboard = ({ subcategoryToken: propSubcategoryToken }) => {

    const {
        lastYearSameMonth,
        currentMonthLabel,
        prevMonthLabel
    } = getTimeSeriesLabels();

    const { setFilterOptions, setFilterChips, filterOptions, setActiveFilters, activeFilters } = useFilterContext();
    const [controller, dispatch] = useMaterialUIController();
    const { miniSidenav } = controller;
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md')); // Below 900px
    const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg')); // Between 900px and 1200px


    // State for API data

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [myDashboardData, setMyDashboardData] = useState(null);
    const [myDashboardfilterData, setMyDashboardfilterData] = useState(null);

    // Filter state for Highlights section time period chips
    const [highlightsActiveIdx, setHighlightsActiveIdx] = React.useState(0);
    const [highlightsActiveChipName, setHighlightsActiveChipName] = useState("YTD");

    // Filter state for Visualizations section time period chips
    const [visualizationActiveIdx, setVisualizationActiveIdx] = React.useState(0);
    const [visualizationActiveChipName, setVisualizationActiveChipName] = useState("YTD");

    useEffect(() => {
        const header = document.querySelector('.MuiAppBar-root');
        if (header) {
            header.style.display = 'none';
        }
    }, []);

    useEffect(() => {
        const fetchAllData = async () => {
            try {
                setLoading(true);
            } catch (error) {
                console.error('Failed loading static mocks or transforming data:', error);
                setError('Failed to load static mock data. Check competitive_analysis/data/allMocks.json');
            } finally {
                setLoading(false);
            }
        };

        fetchAllData();
    }, [location.search, propSubcategoryToken]);



    // Helper function to build GraphQL filters from activeFilters
    const buildGqlFiltersFromActive = useCallback((filters) => {
        const extract = (items) => {
            if (!items || !Array.isArray(items) || items.length === 0) return null;
            // Handle both string arrays and object arrays with name property
            const names = items.map(item => typeof item === 'string' ? item : item?.name).filter(Boolean);
            return names.length > 0 ? { in: names } : null;
        };

        const gqlFiltersRaw = {
            companyName: extract(filters?.Company),
            categoryName: extract(filters?.Category),
            brandName: extract(filters?.Brand),
            subBrandSubcategory: extract(filters?.["Sub-Brand"]),
            subBrandName: extract(filters?.["Sub-Brand"]),
            routeToMarket: extract(filters?.["Route to Market"]),
            channel: extract(filters?.Channel)
        };
        // Remove null/undefined values to avoid backend errors
        return Object.fromEntries(
            Object.entries(gqlFiltersRaw).filter(([_, value]) => value !== null && value !== undefined)
        );
    }, []);

    // Fetch filter options on initial load
    useEffect(() => {
        const fetchFilterOptions = async () => {
            if (filterOptions && Object.keys(filterOptions).length > 0) {
                return; // Already have filter options
            }

            try {
                const myDashboardfilterResponse = await fetchMyDashboardFilter();
                const filterOptionsData = {
                    Company: myDashboardfilterResponse?.companies?.items || [],
                    Category: myDashboardfilterResponse?.categories?.items || [],
                    Brand: myDashboardfilterResponse?.brands?.items || [],
                    "Sub-Brand": myDashboardfilterResponse?.subBrands?.items || [],
                    "Route to Market": myDashboardfilterResponse?.routeToMarkets?.items || [],
                    Channel: myDashboardfilterResponse?.channels?.items || [],
                };
                setFilterOptions(filterOptionsData);
                setMyDashboardfilterData(myDashboardfilterResponse);

                const companyChips = myDashboardfilterResponse?.companies.items || [];
                const categoryChips = myDashboardfilterResponse?.categories?.items || [];
                setFilterChips([
                    { key: "all", name: "All" },
                    ...companyChips,
                    ...categoryChips,
                    { key: "custom", name: "Custom" }
                ]);
            } catch (error) {
                console.error('Failed loading filter options:', error);
            }
        };

        fetchFilterOptions();
    }, [filterOptions, setFilterOptions, setFilterChips]);

    // Fetch dashboard data when filters or time periods change
    useEffect(() => {
        const fetchAllData = async () => {
            // Wait for filter options to be available if they're not loaded yet
            if (!filterOptions || Object.keys(filterOptions).length === 0) {
                return;
            }

            try {
                setLoading(true);

                // Build GraphQL filters from activeFilters if they exist and have selections
                // Otherwise, use all available filter options
                let gqlFilters = {};

                if (activeFilters && Object.values(activeFilters).some(arr => (arr || []).length > 0)) {
                    // Use activeFilters when they have selections
                    gqlFilters = buildGqlFiltersFromActive(activeFilters);
                } else {
                    // Fallback to all filter options if no active filters
                    const extractNames = (items) => {
                        if (!items?.length) return null;
                        return {
                            in: items.map(item => typeof item === 'string' ? item : item?.name).filter(Boolean)
                        };
                    };

                    const gqlFiltersRaw = {
                        companyName: extractNames(filterOptions.Company),
                        categoryName: extractNames(filterOptions.Category),
                        brandName: extractNames(filterOptions.Brand),
                        subBrandSubcategory: extractNames(filterOptions["Sub-Brand"]),
                        subBrandName: extractNames(filterOptions["Sub-Brand"]),
                        routeToMarket: extractNames(filterOptions["Route to Market"]),
                        channel: extractNames(filterOptions.Channel)
                    };
                    gqlFilters = Object.fromEntries(
                        Object.entries(gqlFiltersRaw).filter(([_, value]) => value !== null && value !== undefined)
                    );
                }

                console.log('Fetching dashboard data with filters:', gqlFilters);
                const myDashboardResponse = await fetchMyDashboard(
                    visualizationActiveChipName,
                    highlightsActiveChipName,
                    gqlFilters
                );

                const myDashboardItem = myDashboardResponse || null;
                setMyDashboardData(myDashboardItem);

            } catch (error) {
                console.error('Failed loading my dashboard data:', error);
                setError('Failed to load my dashboard data');
            } finally {
                setLoading(false);
            }
        };

        fetchAllData();
    }, [location.search, propSubcategoryToken, visualizationActiveChipName, highlightsActiveChipName, activeFilters, buildGqlFiltersFromActive, filterOptions]);


    const formatToMillions = (value) =>
        value == null || value === "-" ? "-" : (Number(value) / 1_000_000).toFixed(1);

    const formatMillions = (value) =>
        value == null || value === "-" ? "-" : Number(value).toFixed(1);

    const truncateDecimal = (num, decimals = 1) => {
        const factor = Math.pow(10, decimals);
        return Math.floor(num * factor) / factor;
    };

    //First section KPIscards data
    const Kpiscardsdata = [
        {
            "title": "Net Sales",
            "showIndicator": true,
            "values": [
                {
                    "label": "MTD",
                    "change": myDashboardData?.netSales?.items?.cMvsGoal ? `${myDashboardData?.netSales?.items?.cMvsGoal}%` : "0%",
                    "changely": myDashboardData?.netSales?.items?.cMvsLM ? `${myDashboardData?.netSales?.items?.cMvsLM}%` : "0%",
                    "value": `$${formatToMillions(myDashboardData?.netSales?.items?.currentMonth)}M`,
                    "status": "increase",
                    "title": "Month",
                    "lyValue": `$${formatToMillions(myDashboardData?.netSales?.items?.lastMonth)}M`,
                    "glvalue": `$${formatToMillions(myDashboardData?.netSales?.items?.goalCurrentMonth)}M`
                },
                {
                    "label": "QTD",
                    "change": myDashboardData?.netSales?.items?.cQvsGoal ? `${myDashboardData?.netSales?.items?.cQvsGoal}%` : "0%",
                    "changely": myDashboardData?.netSales?.items?.cQvsLQ ? `${myDashboardData?.netSales?.items?.cQvsLQ}%` : "0%",
                    "value": `$${formatToMillions(myDashboardData?.netSales?.items?.currentQuarter)}M`,
                    "status": "increase",
                    "title": "Quarter",
                    "lyValue": `$${formatToMillions(myDashboardData?.netSales?.items?.lastQuarter)}M`,
                    "glvalue": `$${formatToMillions(myDashboardData?.netSales?.items?.goalCurrentQuarter)}M`
                },
                {
                    "label": "YTD",
                    "change": myDashboardData?.netSales?.items?.cYvsGoal ? `${myDashboardData?.netSales?.items?.cYvsGoal}%` : "0%",
                    "changely": myDashboardData?.netSales?.items?.cYvsLY ? `${myDashboardData?.netSales?.items?.cYvsLY}%` : "0%",
                    "value": `$${formatToMillions(myDashboardData?.netSales?.items?.currentYear)}M`,
                    "status": "increase",
                    "title": "Year",
                    "lyValue": `$${formatToMillions(myDashboardData?.netSales?.items?.lastYear)}M`,
                    "glvalue": `$${formatToMillions(myDashboardData?.netSales?.items?.goalCurrentYear)}M`
                }
            ]
        },
        {
            "title": "Cost of Goods Sold",
            "showIndicator": true,
            "values": [
                {
                    "label": "MTD",
                    "change": myDashboardData?.costOfGoodsSold?.items?.cMvsGoal ? `${myDashboardData?.costOfGoodsSold?.items?.cMvsGoal}%` : "0%",
                    "changely": myDashboardData?.costOfGoodsSold?.items?.cMvsLM ? `${myDashboardData?.costOfGoodsSold?.items?.cMvsLM}%` : "0%",
                    "value": `$${formatToMillions(myDashboardData?.costOfGoodsSold?.items?.currentMonth)}M`,
                    "status": "increase",
                    "title": "Month",
                    "lyValue": `$${formatToMillions(myDashboardData?.costOfGoodsSold?.items?.lastMonth)}M`,
                    "glvalue": `$${formatToMillions(myDashboardData?.costOfGoodsSold?.items?.goalCurrentMonth)}M`
                },
                {
                    "label": "QTD",
                    "change": myDashboardData?.costOfGoodsSold?.items?.cQvsGoal ? `${myDashboardData?.costOfGoodsSold?.items?.cQvsGoal}%` : "0%",
                    "changely": myDashboardData?.costOfGoodsSold?.items?.cQvsLQ ? `${myDashboardData?.costOfGoodsSold?.items?.cQvsLQ}%` : "0%",
                    "value": `$${formatToMillions(myDashboardData?.costOfGoodsSold?.items?.currentQuarter)}M`,
                    "status": "increase",
                    "title": "Quarter",
                    "lyValue": `$${formatToMillions(myDashboardData?.costOfGoodsSold?.items?.lastQuarter)}M`,
                    "glvalue": `$${formatToMillions(myDashboardData?.costOfGoodsSold?.items?.goalCurrentQuarter)}M`
                },
                {
                    "label": "YTD",
                    "change": myDashboardData?.costOfGoodsSold?.items?.cYvsGoal ? `${myDashboardData?.costOfGoodsSold?.items?.cYvsGoal}%` : "0%",
                    "changely": myDashboardData?.costOfGoodsSold?.items?.cYvsLY ? `${myDashboardData?.costOfGoodsSold?.items?.cYvsLY}%` : "0%",
                    "value": `$${formatToMillions(myDashboardData?.costOfGoodsSold?.items?.currentYear)}M`,
                    "status": "increase",
                    "title": "Year",
                    "lyValue": `$${formatToMillions(myDashboardData?.costOfGoodsSold?.items?.lastYear)}M`,
                    "glvalue": `$${formatToMillions(myDashboardData?.costOfGoodsSold?.items?.goalCurrentYear)}M`
                }
            ]
        },
        {
            "title": "Gross Profit",
            "showIndicator": true,
            "values": [
                {
                    "label": "MTD",
                    "change": myDashboardData?.grossProfit?.items?.cMvsGoal ? `${myDashboardData?.grossProfit?.items?.cMvsGoal}%` : "0%",
                    "changely": myDashboardData?.grossProfit?.items?.cMvsLM ? `${myDashboardData?.grossProfit?.items?.cMvsLM}%` : "0%",
                    "value": `$${formatToMillions(myDashboardData?.grossProfit?.items?.currentMonth)}M`,
                    "status": "increase",
                    "title": "Month",
                    "lyValue": `$${formatToMillions(myDashboardData?.grossProfit?.items?.lastMonth)}M`,
                    "glvalue": `$${formatToMillions(myDashboardData?.grossProfit?.items?.goalCurrentMonth)}M`
                },
                {
                    "label": "QTD",
                    "change": myDashboardData?.grossProfit?.items?.cQvsGoal ? `${myDashboardData?.grossProfit?.items?.cQvsGoal}%` : "0%",
                    "changely": myDashboardData?.grossProfit?.items?.cQvsLQ ? `${myDashboardData?.grossProfit?.items?.cQvsLQ}%` : "0%",
                    "value": `$${formatToMillions(myDashboardData?.grossProfit?.items?.currentQuarter)}M`,
                    "status": "increase",
                    "title": "Quarter",
                    "lyValue": `$${formatToMillions(myDashboardData?.grossProfit?.items?.lastQuarter)}M`,
                    "glvalue": `$${formatToMillions(myDashboardData?.grossProfit?.items?.goalCurrentQuarter)}M`
                },
                {
                    "label": "YTD",
                    "change": myDashboardData?.grossProfit?.items?.cYvsGoal ? `${myDashboardData?.grossProfit?.items?.cYvsGoal}%` : "0%",
                    "changely": myDashboardData?.grossProfit?.items?.cYvsLY ? `${myDashboardData?.grossProfit?.items?.cYvsLY}%` : "0%",
                    "value": `$${formatToMillions(myDashboardData?.grossProfit?.items?.currentYear)}M`,
                    "status": "increase",
                    "title": "Year",
                    "lyValue": `$${formatToMillions(myDashboardData?.grossProfit?.items?.lastYear)}M`,
                    "glvalue": `$${formatToMillions(myDashboardData?.grossProfit?.items?.goalCurrentYear)}M`
                }
            ]
        },

        {
            "title": "EBIT",
            "showIndicator": true,
            "values": [
                {
                    "label": "MTD",
                    "change": myDashboardData?.eBit?.items?.cMvsGoal ? `${myDashboardData?.eBit?.items?.cMvsGoal}%` : "0%",
                    "changely": myDashboardData?.eBit?.items?.cMvsLM ? `${myDashboardData?.eBit?.items?.cMvsLM}%` : "0%",
                    "value": `$${formatToMillions(myDashboardData?.eBit?.items?.currentMonth)}M`,
                    "status": "increase",
                    "title": "Month",
                    "lyValue": `$${formatToMillions(myDashboardData?.eBit?.items?.lastMonth)}M`,
                    "glvalue": `$${formatToMillions(myDashboardData?.eBit?.items?.goalCurrentMonth)}M`
                },
                {
                    "label": "QTD",
                    "change": myDashboardData?.eBit?.items?.cQvsGoal ? `${myDashboardData?.eBit?.items?.cQvsGoal}%` : "0%",
                    "changely": myDashboardData?.eBit?.items?.cQvsLQ ? `${myDashboardData?.eBit?.items?.cQvsLQ}%` : "0%",
                    "value": `$${formatToMillions(myDashboardData?.eBit?.items?.currentQuarter)}M`,
                    "status": "increase",
                    "title": "Quarter",
                    "lyValue": `$${formatToMillions(myDashboardData?.eBit?.items?.lastQuarter)}M`,
                    "glvalue": `$${formatToMillions(myDashboardData?.eBit?.items?.goalCurrentQuarter)}M`
                },
                {
                    "label": "YTD",
                    "change": myDashboardData?.eBit?.items?.cYvsGoal ? `${myDashboardData?.eBit?.items?.cYvsGoal}%` : "0%",
                    "changely": myDashboardData?.eBit?.items?.cYvsLY ? `${myDashboardData?.eBit?.items?.cYvsLY}%` : "0%",
                    "value": `$${formatToMillions(myDashboardData?.eBit?.items?.currentYear)}M`,
                    "status": "increase",
                    "title": "Year",
                    "lyValue": `$${formatToMillions(myDashboardData?.eBit?.items?.lastYear)}M`,
                    "glvalue": `$${formatToMillions(myDashboardData?.eBit?.items?.goalCurrentYear)}M`
                }
            ]
        },
        {
            "title": "Closing Balance (Cash Position)",
            "showIndicator": true,
            "values": [
                {
                    "label": "MTD",
                    "change": myDashboardData?.closingBalance?.items?.cMvsGoal ? `${myDashboardData?.closingBalance?.items?.cMvsGoal}%` : "0%",
                    "changely": myDashboardData?.closingBalance?.items?.cMvsLM ? `${myDashboardData?.closingBalance?.items?.cMvsLM}%` : "0%",
                    "value": `$${formatToMillions(myDashboardData?.closingBalance?.items?.currentMonth)}M`,
                    "status": "increase",
                    "title": "Month",
                    "lyValue": `$${formatToMillions(myDashboardData?.closingBalance?.items?.lastMonth)}M`,
                    "glvalue": `$${formatToMillions(myDashboardData?.closingBalance?.items?.goalCurrentMonth)}M`
                },
                {
                    "label": "QTD",
                    "change": myDashboardData?.closingBalance?.items?.cQvsGoal ? `${myDashboardData?.closingBalance?.items?.cQvsGoal}%` : "0%",
                    "changely": myDashboardData?.closingBalance?.items?.cQvsLQ ? `${myDashboardData?.closingBalance?.items?.cQvsLQ}%` : "0%",
                    "value": `$${formatToMillions(myDashboardData?.closingBalance?.items?.currentQuarter)}M`,
                    "status": "increase",
                    "title": "Quarter",
                    "lyValue": `$${formatToMillions(myDashboardData?.closingBalance?.items?.lastQuarter)}M`,
                    "glvalue": `$${formatToMillions(myDashboardData?.closingBalance?.items?.goalCurrentQuarter)}M`
                },
                {
                    "label": "YTD",
                    "change": myDashboardData?.closingBalance?.items?.cYvsGoal ? `${myDashboardData?.closingBalance?.items?.cYvsGoal}%` : "0%",
                    "changely": myDashboardData?.closingBalance?.items?.cYvsLY ? `${myDashboardData?.closingBalance?.items?.cYvsLY}%` : "0%",
                    "value": `$${formatToMillions(myDashboardData?.closingBalance?.items?.currentYear)}M`,
                    "status": "increase",
                    "title": "Year",
                    "lyValue": `$${formatToMillions(myDashboardData?.closingBalance?.items?.lastYear)}M`,
                    "glvalue": `$${formatToMillions(myDashboardData?.closingBalance?.items?.goalCurrentYear)}M`
                }
            ]
        },
        {
            "title": "Stock Amount (Dollars)",
            "showIndicator": true,
            "values": [
                {
                    "label": "MTD",
                    "change": myDashboardData?.stockAmount?.items?.cMvsGoal ? `${myDashboardData?.stockAmount?.items?.cMvsGoal}%` : "0%",
                    "changely": myDashboardData?.stockAmount?.items?.cMvsLM ? `${myDashboardData?.stockAmount?.items?.cMvsLM}%` : "0%",
                    "value": `$${formatToMillions(myDashboardData?.stockAmount?.items?.currentMonth)}M`,
                    "status": "increase",
                    "title": "Month",
                    "lyValue": `$${formatToMillions(myDashboardData?.stockAmount?.items?.lastMonth)}M`,
                    "glvalue": `$${formatToMillions(myDashboardData?.stockAmount?.items?.goalCurrentMonth)}M`
                },
                {
                    "label": "QTD",
                    "change": myDashboardData?.stockAmount?.items?.cQvsGoal ? `${myDashboardData?.stockAmount?.items?.cQvsGoal}%` : "0%",
                    "changely": myDashboardData?.stockAmount?.items?.cQvsLQ ? `${myDashboardData?.stockAmount?.items?.cQvsLQ}%` : "0%",
                    "value": `$${formatToMillions(myDashboardData?.stockAmount?.items?.currentQuarter)}M`,
                    "status": "increase",
                    "title": "Quarter",
                    "lyValue": `$${formatToMillions(myDashboardData?.stockAmount?.items?.lastQuarter)}M`,
                    "glvalue": `$${formatToMillions(myDashboardData?.stockAmount?.items?.goalCurrentQuarter)}M`
                },
                {
                    "label": "YTD",
                    "change": myDashboardData?.stockAmount?.items?.cYvsGoal ? `${myDashboardData?.stockAmount?.items?.cYvsGoal}%` : "0%",
                    "changely": myDashboardData?.stockAmount?.items?.cYvsLY ? `${myDashboardData?.stockAmount?.items?.cYvsLY}%` : "0%",
                    "value": `$${formatToMillions(myDashboardData?.stockAmount?.items?.currentYear)}M`,
                    "status": "increase",
                    "title": "Year",
                    "lyValue": `$${formatToMillions(myDashboardData?.stockAmount?.items?.lastYear)}M`,
                    "glvalue": `$${formatToMillions(myDashboardData?.stockAmount?.items?.goalCurrentYear)}M`
                }
            ]
        },

        {
            "title": "Inventory Turnover (Units)",
            "showIndicator": false,
            "values": [
                {
                    "label": "MTD",
                    "change": myDashboardData?.inventoryTurnOverUnits?.items?.cMvsGoal ? `${myDashboardData?.inventoryTurnOverUnits?.items?.cMvsGoal}%` : "0%",
                    "changely": myDashboardData?.inventoryTurnOverUnits?.items?.cMvsLM ? `${myDashboardData?.inventoryTurnOverUnits?.items?.cMvsLM}%` : "0%",
                    "value": `$${formatToMillions(myDashboardData?.inventoryTurnOverUnits?.items?.currentMonth)}M`,
                    "status": "increase",
                    "title": "Month",
                    "lyValue": `$${formatToMillions(myDashboardData?.inventoryTurnOverUnits?.items?.lastMonth)}M`,
                    "glvalue": `$${formatToMillions(myDashboardData?.inventoryTurnOverUnits?.items?.goalCurrentMonth)}M`
                },
                {
                    "label": "QTD",
                    "change": myDashboardData?.inventoryTurnOverUnits?.items?.cQvsGoal ? `${myDashboardData?.inventoryTurnOverUnits?.items?.cQvsGoal}%` : "0%",
                    "changely": myDashboardData?.inventoryTurnOverUnits?.items?.cQvsLQ ? `${myDashboardData?.inventoryTurnOverUnits?.items?.cQvsLQ}%` : "0%",
                    "value": `$${formatToMillions(myDashboardData?.inventoryTurnOverUnits?.items?.currentQuarter)}M`,
                    "status": "increase",
                    "title": "Quarter",
                    "lyValue": `$${formatToMillions(myDashboardData?.inventoryTurnOverUnits?.items?.lastQuarter)}M`,
                    "glvalue": `$${formatToMillions(myDashboardData?.inventoryTurnOverUnits?.items?.goalCurrentQuarter)}M`
                },
                {
                    "label": "YTD",
                    "change": myDashboardData?.inventoryTurnOverUnits?.items?.cYvsGoal ? `${myDashboardData?.inventoryTurnOverUnits?.items?.cYvsGoal}%` : "0%",
                    "changely": myDashboardData?.inventoryTurnOverUnits?.items?.cYvsLY ? `${myDashboardData?.inventoryTurnOverUnits?.items?.cYvsLY}%` : "0%",
                    "value": `$${formatToMillions(myDashboardData?.inventoryTurnOverUnits?.items?.currentYear)}M`,
                    "status": "increase",
                    "title": "Year",
                    "lyValue": `$${formatToMillions(myDashboardData?.inventoryTurnOverUnits?.items?.lastYear)}M`,
                    "glvalue": `$${formatToMillions(myDashboardData?.inventoryTurnOverUnits?.items?.goalCurrentYear)}M`
                }
            ]
        },
        {
            "title": "Forecast Accuracy (MAPE)",
            "showIndicator": false,
            "values": [
                {
                    "label": "MTD",
                    "change": myDashboardData?.forecastAccuracy?.items?.cMvsGoal ? `${myDashboardData?.forecastAccuracy?.items?.cMvsGoal}%` : "0%",
                    "changely": myDashboardData?.forecastAccuracy?.items?.cMvsLM ? `${myDashboardData?.forecastAccuracy?.items?.cMvsLM}%` : "0%",
                    "value": `$${formatToMillions(myDashboardData?.forecastAccuracy?.items?.currentMonth)}M`,
                    "status": "increase",
                    "title": "Month",
                    "lyValue": `$${formatToMillions(myDashboardData?.forecastAccuracy?.items?.lastMonth)}M`,
                    "glvalue": `$${formatToMillions(myDashboardData?.forecastAccuracy?.items?.goalCurrentMonth)}M`
                },
                {
                    "label": "QTD",
                    "change": myDashboardData?.forecastAccuracy?.items?.cQvsGoal ? `${myDashboardData?.forecastAccuracy?.items?.cQvsGoal}%` : "0%",
                    "changely": myDashboardData?.forecastAccuracy?.items?.cQvsLQ ? `${myDashboardData?.forecastAccuracy?.items?.cQvsLQ}%` : "0%",
                    "value": `$${formatToMillions(myDashboardData?.forecastAccuracy?.items?.currentQuarter)}M`,
                    "status": "increase",
                    "title": "Quarter",
                    "lyValue": `$${formatToMillions(myDashboardData?.forecastAccuracy?.items?.lastQuarter)}M`,
                    "glvalue": `$${formatToMillions(myDashboardData?.forecastAccuracy?.items?.goalCurrentQuarter)}M`
                },
                {
                    "label": "YTD",
                    "change": myDashboardData?.forecastAccuracy?.items?.cYvsGoal ? `${myDashboardData?.forecastAccuracy?.items?.cYvsGoal}%` : "0%",
                    "changely": myDashboardData?.forecastAccuracy?.items?.cYvsLY ? `${myDashboardData?.forecastAccuracy?.items?.cYvsLY}%` : "0%",
                    "value": `$${formatToMillions(myDashboardData?.forecastAccuracy?.items?.currentYear)}M`,
                    "status": "increase",
                    "title": "Year",
                    "lyValue": `$${formatToMillions(myDashboardData?.forecastAccuracy?.items?.lastYear)}M`,
                    "glvalue": `$${formatToMillions(myDashboardData?.forecastAccuracy?.items?.goalCurrentYear)}M`
                }
            ]
        },
        {
            "title": "Freight as a Percentage of Revenue",
            "showIndicator": false,
            "values": [
                {
                    "label": "MTD",
                    "change": myDashboardData?.freightPercentOfRevenue?.items?.cMvsGoal ? `${myDashboardData?.freightPercentOfRevenue?.items?.cMvsGoal}%` : "0%",
                    "changely": myDashboardData?.freightPercentOfRevenue?.items?.cMvsLM ? `${myDashboardData?.freightPercentOfRevenue?.items?.cMvsLM}%` : "0%",
                    "value": `$${formatToMillions(myDashboardData?.freightPercentOfRevenue?.items?.currentMonth)}M`,
                    "status": "increase",
                    "title": "Month",
                    "lyValue": `$${formatToMillions(myDashboardData?.freightPercentOfRevenue?.items?.lastMonth)}M`,
                    "glvalue": `$${formatToMillions(myDashboardData?.freightPercentOfRevenue?.items?.goalCurrentMonth)}M`
                },
                {
                    "label": "QTD",
                    "change": myDashboardData?.freightPercentOfRevenue?.items?.cQvsGoal ? `${myDashboardData?.freightPercentOfRevenue?.items?.cQvsGoal}%` : "0%",
                    "changely": myDashboardData?.freightPercentOfRevenue?.items?.cQvsLQ ? `${myDashboardData?.freightPercentOfRevenue?.items?.cQvsLQ}%` : "0%",
                    "value": `$${formatToMillions(myDashboardData?.freightPercentOfRevenue?.items?.currentQuarter)}M`,
                    "status": "increase",
                    "title": "Quarter",
                    "lyValue": `$${formatToMillions(myDashboardData?.freightPercentOfRevenue?.items?.lastQuarter)}M`,
                    "glvalue": `$${formatToMillions(myDashboardData?.freightPercentOfRevenue?.items?.goalCurrentQuarter)}M`
                },
                {
                    "label": "YTD",
                    "change": myDashboardData?.freightPercentOfRevenue?.items?.cYvsGoal ? `${myDashboardData?.freightPercentOfRevenue?.items?.cYvsGoal}%` : "0%",
                    "changely": myDashboardData?.freightPercentOfRevenue?.items?.cYvsLY ? `${myDashboardData?.freightPercentOfRevenue?.items?.cYvsLY}%` : "0%",
                    "value": `$${formatToMillions(myDashboardData?.freightPercentOfRevenue?.items?.currentYear)}M`,
                    "status": "decrease",
                    "title": "Year",
                    "lyValue": `$${formatToMillions(myDashboardData?.freightPercentOfRevenue?.items?.lastYear)}M`,
                    "glvalue": `$${formatToMillions(myDashboardData?.freightPercentOfRevenue?.items?.goalCurrentYear)}M`
                }
            ]
        }
    ]

    //Second section TimeSeriescards data
    const timeSeriesCardsdata = [
        {
            title: "Net Sales",
            values: [
                {
                    label: lastYearSameMonth,
                    change: myDashboardData?.netSales?.items?.cmcYvsCMLY ? `${truncateDecimal(myDashboardData?.netSales?.items?.cmcYvsCMLY)}%` : "0%",
                    value: `$${formatToMillions(myDashboardData?.netSales?.items?.sameMonthLastYear)}M`,
                    status: "increase",
                    arrow: "right"
                },
                {
                    label: currentMonthLabel,
                    change: "",
                    value: `$${formatToMillions(myDashboardData?.netSales?.items?.currentMonth)}M`,
                    status: "nochange",
                    arrow: null
                },
                {
                    label: prevMonthLabel,
                    change: myDashboardData?.netSales?.items?.cMvsLM ? `${truncateDecimal(myDashboardData?.netSales?.items?.cMvsLM)}%` : "0%",
                    value: `$${formatToMillions(myDashboardData?.netSales?.items?.lastMonth)}M`,
                    status: "decrease",
                    arrow: "left"
                }
            ],
            "showIndicator": true,
        },

        {
            title: "Cost of Goods Sold",
            values: [
                {
                    label: lastYearSameMonth,
                    change: myDashboardData?.costOfGoodsSold?.items?.cmcYvsCMLY ? `${truncateDecimal(myDashboardData?.costOfGoodsSold?.items?.cmcYvsCMLY)}%` : "0%",
                    value: `$${formatToMillions(myDashboardData?.costOfGoodsSold?.items?.sameMonthLastYear)}M`,
                    status: "increase",
                    arrow: "right"
                },
                {
                    label: currentMonthLabel,
                    change: "",
                    value: `$${formatToMillions(myDashboardData?.costOfGoodsSold?.items?.currentMonth)}M`,
                    status: "nochange",
                    arrow: null
                },
                {
                    label: prevMonthLabel,
                    change: myDashboardData?.costOfGoodsSold?.items?.cMvsLM ? `${truncateDecimal(myDashboardData?.costOfGoodsSold?.items?.cMvsLM)}%` : "0%",
                    value: `$${formatToMillions(myDashboardData?.costOfGoodsSold?.items?.lastMonth)}M`,
                    status: "decrease",
                    arrow: "left"
                }
            ],
            "showIndicator": true,
        },

        {
            title: "Gross Profit",
            values: [
                {
                    label: lastYearSameMonth,
                    change: myDashboardData?.grossProfit?.items?.cmcYvsCMLY ? `${truncateDecimal(myDashboardData?.grossProfit?.items?.cmcYvsCMLY)}%` : "0%",
                    value: `$${formatToMillions(myDashboardData?.grossProfit?.items?.sameMonthLastYear)}M`,
                    status: "increase",
                    arrow: "right"
                },
                {
                    label: currentMonthLabel,
                    change: "",
                    value: `$${formatToMillions(myDashboardData?.grossProfit?.items?.currentMonth)}M`,
                    status: "nochange",
                    arrow: null
                },
                {
                    label: prevMonthLabel,
                    change: myDashboardData?.grossProfit?.items?.cMvsLM ? `${truncateDecimal(myDashboardData?.grossProfit?.items?.cMvsLM)}%` : "0%",
                    value: `$${formatToMillions(myDashboardData?.grossProfit?.items?.lastMonth)}M`,
                    status: "decrease",
                    arrow: "left"
                }
            ],
            "showIndicator": true,
        },

        {
            title: "EBIT",
            values: [
                {
                    label: lastYearSameMonth,
                    change: myDashboardData?.eBit?.items?.cmcYvsCMLY ? `${truncateDecimal(myDashboardData?.eBit?.items?.cmcYvsCMLY)}%` : "0%",
                    value: `$${formatToMillions(myDashboardData?.eBit?.items?.sameMonthLastYear)}M`,
                    status: "increase",
                    arrow: "right"
                },
                {
                    label: currentMonthLabel,
                    change: "",
                    value: `$${formatToMillions(myDashboardData?.eBit?.items?.currentMonth)}M`,
                    status: "nochange",
                    arrow: null
                },
                {
                    label: prevMonthLabel,
                    change: myDashboardData?.eBit?.items?.cMvsLM ? `${truncateDecimal(myDashboardData?.eBit?.items?.cMvsLM)}%` : "0%",
                    value: `$${formatToMillions(myDashboardData?.eBit?.items?.lastMonth)}M`,
                    status: "decrease",
                    arrow: "left"
                }
            ],
            "showIndicator": true,
        },

        {
            title: "Closing Balance (Cash Position)",
            values: [
                {
                    label: lastYearSameMonth,
                    change: myDashboardData?.closingBalance?.items?.cmcYvsCMLY ? `${truncateDecimal(myDashboardData?.closingBalance?.items?.cmcYvsCMLY)}%` : "0%",
                    value: `$${formatToMillions(myDashboardData?.closingBalance?.items?.sameMonthLastYear)}M`,
                    status: "increase",
                    arrow: "right"
                },
                {
                    label: currentMonthLabel,
                    change: "",
                    value: `$${formatToMillions(myDashboardData?.closingBalance?.items?.currentMonth)}M`,
                    status: "nochange",
                    arrow: null
                },
                {
                    label: prevMonthLabel,
                    change: myDashboardData?.closingBalance?.items?.cMvsLM ? `${truncateDecimal(myDashboardData?.closingBalance?.items?.cMvsLM)}%` : "0%",
                    value: `$${formatToMillions(myDashboardData?.closingBalance?.items?.lastMonth)}M`,
                    status: "decrease",
                    arrow: "left"
                }
            ],
            "showIndicator": true,
        },

        {
            title: "Stock Amount (Dollars)",
            values: [
                {
                    label: lastYearSameMonth,
                    change: myDashboardData?.stockAmount?.items?.cmcYvsCMLY ? `${truncateDecimal(myDashboardData?.stockAmount?.items?.cmcYvsCMLY)}%` : "0%",
                    value: `$${formatToMillions(myDashboardData?.stockAmount?.items?.sameMonthLastYear)}M`,
                    status: "increase",
                    arrow: "right"
                },
                {
                    label: currentMonthLabel,
                    change: "",
                    value: `$${formatToMillions(myDashboardData?.stockAmount?.items?.currentMonth)}M`,
                    status: "nochange",
                    arrow: null
                },
                {
                    label: prevMonthLabel,
                    change: myDashboardData?.stockAmount?.items?.cMvsLM ? `${truncateDecimal(myDashboardData?.stockAmount?.items?.cMvsLM)}%` : "0%",
                    value: `$${formatToMillions(myDashboardData?.stockAmount?.items?.lastMonth)}M`,
                    status: "decrease",
                    arrow: "left"
                }
            ],
            "showIndicator": true,
        },

        {
            title: "Inventory Turnover (Units)",
            values: [
                {
                    label: lastYearSameMonth,
                    change: myDashboardData?.inventoryTurnOverUnits?.items?.cmcYvsCMLY ? `${truncateDecimal(myDashboardData?.inventoryTurnOverUnits?.items?.cmcYvsCMLY)}%` : "0%",
                    value: `$${formatToMillions(myDashboardData?.inventoryTurnOverUnits?.items?.sameMonthLastYear)}M`,
                    status: "increase",
                    arrow: "right"
                },
                {
                    label: currentMonthLabel,
                    change: "",
                    value: `$${formatToMillions(myDashboardData?.inventoryTurnOverUnits?.items?.currentMonth)}M`,
                    status: "nochange",
                    arrow: null
                },
                {
                    label: prevMonthLabel,
                    change: myDashboardData?.inventoryTurnOverUnits?.items?.cMvsLM ? `${truncateDecimal(myDashboardData?.inventoryTurnOverUnits?.items?.cMvsLM)}%` : "0%",
                    value: `$${formatToMillions(myDashboardData?.inventoryTurnOverUnits?.items?.lastMonth)}M`,
                    status: "increase",
                    arrow: "left"
                }
            ],
            "showIndicator": false,
        },

        {
            title: "Forecast Accuracy (MAPE)",
            values: [
                {
                    label: lastYearSameMonth,
                    change: myDashboardData?.forecastAccuracy?.items?.cmcYvsCMLY ? `${truncateDecimal(myDashboardData?.forecastAccuracy?.items?.cmcYvsCMLY)}%` : "0%",
                    value: `$${formatToMillions(myDashboardData?.forecastAccuracy?.items?.sameMonthLastYear)}M`,
                    status: "increase",
                    arrow: "right"
                },
                {
                    label: currentMonthLabel,
                    change: "",
                    value: `$${formatToMillions(myDashboardData?.forecastAccuracy?.items?.currentMonth)}M`,
                    status: "nochange",
                    arrow: null
                },
                {
                    label: prevMonthLabel,
                    change: myDashboardData?.forecastAccuracy?.items?.cMvsLM ? `${truncateDecimal(myDashboardData?.forecastAccuracy?.items?.cMvsLM)}%` : "0%",
                    value: `$${formatToMillions(myDashboardData?.forecastAccuracy?.items?.lastMonth)}M`,
                    status: "increase",
                    arrow: "left"
                }
            ],
            "showIndicator": false,
        },

        {
            title: "Freight as a Percentage of Revenue",
            values: [
                {
                    label: lastYearSameMonth,
                    change: myDashboardData?.freightPercentOfRevenue?.items?.cmcYvsCMLY ? `${truncateDecimal(myDashboardData?.freightPercentOfRevenue?.items?.cmcYvsCMLY)}%` : "0%",
                    value: `$${formatToMillions(myDashboardData?.freightPercentOfRevenue?.items?.sameMonthLastYear)}M`,
                    status: "increase",
                    arrow: "right"
                },
                {
                    label: currentMonthLabel,
                    change: "",
                    value: `$${formatToMillions(myDashboardData?.freightPercentOfRevenue?.items?.currentMonth)}M`,
                    status: "nochange",
                    arrow: null
                },
                {
                    label: prevMonthLabel,
                    change: myDashboardData?.freightPercentOfRevenue?.items?.cMvsLM ? `${truncateDecimal(myDashboardData?.freightPercentOfRevenue?.items?.cMvsLM)}%` : "0%",
                    value: `$${formatToMillions(myDashboardData?.freightPercentOfRevenue?.items?.lastMonth)}M`,
                    status: "decrease",
                    arrow: "left"
                }
            ],
            "showIndicator": false,
        }
    ];

    //Third section highlightCards 
    const highlightCards = [
        {
            title: "#1 Sub Brand by Profitability",
            topLeftLabel: "UNITS SOLD",
            topLeftValue: `$${formatMillions(myDashboardData?.profitabilityBySubBrand?.items[0]?.unitSold)}M`,
            topRightLabel: "NET SALES",
            topRightValue: `$${formatMillions(myDashboardData?.profitabilityBySubBrand?.items[0]?.netSales)}M`,
            bigTitle: myDashboardData?.profitabilityBySubBrand?.items[0]?.name,
            bottomLabel: "UNITS SOLD LY",
            bottomValue: `$${formatMillions(myDashboardData?.profitabilityBySubBrand?.items[0]?.lastPeriodNetSales)}M`
        },
        {
            title: "#2 Sub Brand by Profitability",
            topLeftLabel: "UNITS SOLD",
            topLeftValue: `$${formatMillions(myDashboardData?.profitabilityBySubBrand?.items[1]?.unitSold)}M`,
            topRightLabel: "NET SALES",
            topRightValue: `$${formatMillions(myDashboardData?.profitabilityBySubBrand?.items[1]?.netSales)}M`,
            bigTitle: myDashboardData?.profitabilityBySubBrand?.items[1]?.name,
            bottomLabel: "UNITS SOLD LY",
            bottomValue: `$${formatMillions(myDashboardData?.profitabilityBySubBrand?.items[1]?.lastPeriodNetSales)}M`
        },
        {
            title: "#3 Sub Brand by Profitability",
            topLeftLabel: "UNITS SOLD",
            topLeftValue: `$${formatMillions(myDashboardData?.profitabilityBySubBrand?.items[2]?.unitSold)}M`,
            topRightLabel: "NET SALES",
            topRightValue: `$${formatMillions(myDashboardData?.profitabilityBySubBrand?.items[2]?.netSales)}M`,
            bigTitle: myDashboardData?.profitabilityBySubBrand?.items[2]?.name,
            bottomLabel: "UNITS SOLD LY",
            bottomValue: `$${formatMillions(myDashboardData?.profitabilityBySubBrand?.items[2]?.lastPeriodNetSales)}M`
        },

        {
            title: "#1 Distributor by Profitability",
            topLeftLabel: "UNITS SOLD",
            topLeftValue: `$${formatMillions(myDashboardData?.profitabilityByDistributorByFinancial?.items[0]?.unitSold)}M`,
            topRightLabel: "NET SALES",
            topRightValue: `$${formatMillions(myDashboardData?.profitabilityByDistributorByFinancial?.items[0]?.netSales)}M`,
            bigTitle: myDashboardData?.profitabilityByDistributorByFinancial?.items[0]?.name,
            bottomLabel: "UNITS SOLD LY",
            bottomValue: `$${formatMillions(myDashboardData?.profitabilityByDistributorByFinancial?.items[0]?.lastPeriodNetSales)}M`
        },
        {
            title: "#2 Distributor by Profitability",
            topLeftLabel: "UNITS SOLD",
            topLeftValue: `$${formatMillions(myDashboardData?.profitabilityByDistributorByFinancial?.items[1]?.unitSold)}M`,
            topRightLabel: "NET SALES",
            topRightValue: `$${formatMillions(myDashboardData?.profitabilityByDistributorByFinancial?.items[1]?.netSales)}M`,
            bigTitle: myDashboardData?.profitabilityByDistributorByFinancial?.items[1]?.name,
            bottomLabel: "UNITS SOLD LY",
            bottomValue: `$${formatMillions(myDashboardData?.profitabilityByDistributorByFinancial?.items[1]?.lastPeriodNetSales)}M`
        },
        {
            title: "#3 Distributor by Profitability",
            topLeftLabel: "UNITS SOLD",
            topLeftValue: `$${formatMillions(myDashboardData?.profitabilityByDistributorByFinancial?.items[2]?.unitSold)}M`,
            topRightLabel: "NET SALES",
            topRightValue: `$${formatMillions(myDashboardData?.profitabilityByDistributorByFinancial?.items[2]?.netSales)}M`,
            bigTitle: myDashboardData?.profitabilityByDistributorByFinancial?.items[2]?.name,
            bottomLabel: "UNITS SOLD LY",
            bottomValue: `$${formatMillions(myDashboardData?.profitabilityByDistributorByFinancial?.items[2]?.lastPeriodNetSales)}M`
        },

        {
            title: "#1 Retailer by Profitability",
            topLeftLabel: "UNITS SOLD",
            topLeftValue: `$${formatMillions(myDashboardData?.profitabilityByRetailerByFinancial?.items[0]?.unitSold)}M`,
            topRightLabel: "NET SALES",
            topRightValue: `$${formatMillions(myDashboardData?.profitabilityByRetailerByFinancial?.items[0]?.netSales)}M`,
            bigTitle: myDashboardData?.profitabilityByRetailerByFinancial?.items[0]?.name,
            bottomLabel: "UNITS SOLD LY",
            bottomValue: `$${formatMillions(myDashboardData?.profitabilityByRetailerByFinancial?.items[0]?.lastPeriodNetSales)}M`
        },
        {
            title: "#2 Retailer by Profitability",
            topLeftLabel: "UNITS SOLD",
            topLeftValue: `$${formatMillions(myDashboardData?.profitabilityByRetailerByFinancial?.items[1]?.unitSold)}M`,
            topRightLabel: "NET SALES",
            topRightValue: `$${formatMillions(myDashboardData?.profitabilityByRetailerByFinancial?.items[1]?.netSales)}M`,
            bigTitle: myDashboardData?.profitabilityByRetailerByFinancial?.items[1]?.name,
            bottomLabel: "UNITS SOLD LY",
            bottomValue: `$${formatMillions(myDashboardData?.profitabilityByRetailerByFinancial?.items[1]?.lastPeriodNetSales)}M`
        },
        {
            title: "#3 Retailer by Profitability",
            topLeftLabel: "UNITS SOLD",
            topLeftValue: `$${formatMillions(myDashboardData?.profitabilityByRetailerByFinancial?.items[2]?.unitSold)}M`,
            topRightLabel: "NET SALES",
            topRightValue: `$${formatMillions(myDashboardData?.profitabilityByRetailerByFinancial?.items[2]?.netSales)}M`,
            bigTitle: myDashboardData?.profitabilityByRetailerByFinancial?.items[2]?.name,
            bottomLabel: "UNITS SOLD LY",
            bottomValue: `$${formatMillions(myDashboardData?.profitabilityByRetailerByFinancial?.items[2]?.lastPeriodNetSales)}M`
        },

        {
            title: "#1 Backordered SKU",
            topLeftLabel: "UNITS SOLD",
            topLeftValue: `$${formatMillions(myDashboardData?.profitabilityBySku?.items[0]?.unitSold)}M`,
            topRightLabel: "NET SALES",
            topRightValue: `$${formatMillions(myDashboardData?.profitabilityBySku?.items[0]?.netSales)}M`,
            bigTitle: myDashboardData?.profitabilityBySku?.items[0]?.name,
            bottomLabel: "UNITS SOLD LY",
            bottomValue: `$${formatMillions(myDashboardData?.profitabilityBySku?.items[0]?.lastPeriodNetSales)}M`
        },
        {
            title: "#2 Backordered SKU",
            topLeftLabel: "UNITS SOLD",
            topLeftValue: `$${formatMillions(myDashboardData?.profitabilityBySku?.items[1]?.unitSold)}M`,
            topRightLabel: "NET SALES",
            topRightValue: `$${formatMillions(myDashboardData?.profitabilityBySku?.items[1]?.netSales)}M`,
            bigTitle: myDashboardData?.profitabilityBySku?.items[1]?.name,
            bottomLabel: "UNITS SOLD LY",
            bottomValue: `$${formatMillions(myDashboardData?.profitabilityBySku?.items[1]?.lastPeriodNetSales)}M`
        },
        {
            title: "#3 Backordered SKU",
            topLeftLabel: "UNITS SOLD",
            topLeftValue: `$${formatMillions(myDashboardData?.profitabilityBySku?.items[2]?.unitSold)}M`,
            topRightLabel: "NET SALES",
            topRightValue: `$${formatMillions(myDashboardData?.profitabilityBySku?.items[2]?.netSales)}M`,
            bigTitle: myDashboardData?.profitabilityBySku?.items[2]?.name,
            bottomLabel: "UNITS SOLD LY",
            bottomValue: `$${formatMillions(myDashboardData?.profitabilityBySku?.items[2]?.lastPeriodNetSales)}M`
        }
    ];



    const [openFilters, setOpenFilters] = useState(false);
    const handleToggleFilters = () => {
        setOpenFilters((prev) => !prev);
    };

    // Filter state
    const [activeFilter, setActiveFilter] = useState('All');

    // Handler for Highlights section chips
    const handleHighlightsChipClick = (idx, label) => {
        setHighlightsActiveIdx(idx);
        setHighlightsActiveChipName(label);
        updateSelectedChipNameHighlights(label);
        console.log("Clicked Highlights Chip Name:", label);
    };

    // Handler for Visualizations section chips
    const handleVisualizationChipClick = (idx, label) => {
        setVisualizationActiveIdx(idx);
        setVisualizationActiveChipName(label);
        console.log("Clicked Visualization Chip Name:", label);
    };

    const [filterLoading, setFilterLoading] = useState(false);

    // Define filter presets for each quick filter using dynamic filterOptions
    const getFilterPreset = useCallback((filterName) => {
        if (!filterOptions) return null;

        // Helper to extract names from filter options
        const getNames = (key) => (filterOptions[key] || []).map(v => v?.name || v);

        // Get all available options dynamically
        const allCompanies = getNames("Company");
        const allCategories = getNames("Category");
        const allBrands = getNames("Brand");
        const allSubBrands = getNames("Sub-Brand");
        const allRoutes = getNames("Route to Market");
        const allChannels = getNames("Channel");

        // Helper to get Nutrabolt brands/sub-brands (exclude Bloom)
        const getNutraboltBrands = () => {
            return allBrands
                .filter(brand => {
                    const brandName = typeof brand === 'string' ? brand : brand?.name;
                    return brandName && !brandName.toLowerCase().includes('bloom');
                })
                .map(b => typeof b === 'string' ? b : b?.name);
        };

        const getNutraboltSubBrands = () => {
            return allSubBrands
                .filter(subBrand => {
                    const subBrandName = typeof subBrand === 'string' ? subBrand : subBrand?.name;
                    return subBrandName && !subBrandName.toLowerCase().includes('bloom');
                })
                .map(sb => typeof sb === 'string' ? sb : sb?.name);
        };

        // Helper to get Bloom brands/sub-brands
        const getBloomBrands = () => {
            return allBrands
                .filter(brand => {
                    const brandName = typeof brand === 'string' ? brand : brand?.name;
                    return brandName && brandName.toLowerCase().includes('bloom');
                })
                .map(b => typeof b === 'string' ? b : b?.name);
        };

        const getBloomSubBrands = () => {
            return allSubBrands
                .filter(subBrand => {
                    const subBrandName = typeof subBrand === 'string' ? subBrand : subBrand?.name;
                    return subBrandName && subBrandName.toLowerCase().includes('bloom');
                })
                .map(sb => typeof sb === 'string' ? sb : sb?.name);
        };

        // Helper to get Functional Beverage sub-brands (beverage products, exclude powders)
        const getFunctionalBeverageSubBrands = () => {
            return allSubBrands
                .filter(subBrand => {
                    const subBrandName = typeof subBrand === 'string' ? subBrand : subBrand?.name;
                    // Exclude powder/supplement sub-brands
                    return subBrandName &&
                        !subBrandName.toLowerCase().includes('powder') &&
                        !subBrandName.toLowerCase().includes('cellucor') &&
                        subBrandName !== 'XTEND' &&
                        subBrandName !== 'C4';
                })
                .map(sb => typeof sb === 'string' ? sb : sb?.name);
        };

        // Helper to get Active Nutrition sub-brands (supplements/powders)
        const getActiveNutritionSubBrands = () => {
            return allSubBrands
                .filter(subBrand => {
                    const subBrandName = typeof subBrand === 'string' ? subBrand : subBrand?.name;
                    // Include powder and supplement sub-brands
                    return subBrandName && (
                        subBrandName.toLowerCase().includes('powder') ||
                        subBrandName === 'XTEND' ||
                        subBrandName === 'C4' ||
                        subBrandName.toLowerCase().includes('cellucor')
                    );
                })
                .map(sb => typeof sb === 'string' ? sb : sb?.name);
        };

        // Helper to find category by name
        const findCategory = (searchTerm) => {
            return allCategories.find(cat => {
                const catName = typeof cat === 'string' ? cat : cat?.name;
                return catName && catName.toLowerCase().includes(searchTerm.toLowerCase());
            });
        };

        switch (filterName) {
            case 'All':
                return {
                    Company: allCompanies,
                    Category: allCategories,
                    Brand: allBrands,
                    "Sub-Brand": allSubBrands,
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };
            case 'Nutrabolt':
                return {
                    Company: ["Nutrabolt"],
                    Category: allCategories,
                    Brand: getNutraboltBrands(),
                    "Sub-Brand": getNutraboltSubBrands(),
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };
            case 'Bloom':
                const bloomCategory = findCategory('functional');
                return {
                    Company: ["Bloom"],
                    Category: bloomCategory ? [typeof bloomCategory === 'string' ? bloomCategory : bloomCategory?.name] : [],
                    Brand: getBloomBrands(),
                    "Sub-Brand": getBloomSubBrands(),
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };
            case 'Functional Beverage':
                const functionalBeverageCategory = findCategory('functional');
                const functionalBeverageBrands = allBrands
                    .filter(brand => {
                        const brandName = typeof brand === 'string' ? brand : brand?.name;
                        return brandName && (brandName === 'C4' || brandName.toLowerCase().includes('bloom'));
                    })
                    .map(b => typeof b === 'string' ? b : b?.name);
                return {
                    Company: allCompanies,
                    Category: functionalBeverageCategory ? [typeof functionalBeverageCategory === 'string' ? functionalBeverageCategory : functionalBeverageCategory?.name] : [],
                    Brand: functionalBeverageBrands,
                    "Sub-Brand": getFunctionalBeverageSubBrands(),
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };
            case 'Active Nutrition':
                const activeNutritionCategory = findCategory('active');
                return {
                    Company: ["Nutrabolt"],
                    Category: activeNutritionCategory ? [typeof activeNutritionCategory === 'string' ? activeNutritionCategory : activeNutritionCategory?.name] : [],
                    Brand: getNutraboltBrands(),
                    "Sub-Brand": getActiveNutritionSubBrands(),
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };
            default:
                return null;
        }
    }, [filterOptions]);

    const [filterPresets, setFilterPresets] = useState(null);

    // Initialize filter presets when filterOptions are available
    useEffect(() => {
        if (filterOptions && !filterPresets) {
            const allPreset = getFilterPreset('All');
            if (allPreset) {
                setFilterPresets(allPreset);
            }
        }
    }, [filterOptions, filterPresets, getFilterPreset]);

    // Handle filter click with full-page loader
    const handleFilterClick = (filter) => {
        if (filter === activeFilter || filterLoading) return;

        setFilterLoading(true);
        setLoading(true); // Show full-page loader

        // Simulate loading delay
        setTimeout(() => {
            const preset = getFilterPreset(filter);
            setActiveFilter(filter);

            // Set the preset - this will trigger FilterPanelContainer to apply it
            if (preset) {
                setFilterPresets(preset);
                // Update FilterContext activeFilters so Header's FilterPanelContainer reflects the change
                if (setActiveFilters) {
                    setActiveFilters(preset);
                }
            } else {
                setFilterPresets(null);
            }

            setFilterLoading(false);
            setLoading(false); // Hide full-page loader

            // Open filter panel for "Custom" filter
            if (filter === 'Custom') {
                setOpenFilters(true);
            }
        }, 800);
    };

    // Handle filter changes from FilterPanelContainer
    const handleFiltersChange = useCallback((newFilters) => {
        // Don't check for preset match if we're already in Custom mode
        // FilterPanelContainer's isApplyingPresetRef prevents callback when applying presets
        if (activeFilter === 'Custom') return;

        // Check if the current filters match any preset
        const presetNames = ['All', 'Nutrabolt', 'Bloom', 'Functional Beverage', 'Active Nutrition'];
        let matchedPreset = null;

        for (const presetName of presetNames) {
            const preset = getFilterPreset(presetName);
            if (preset) {
                const isMatching = Object.keys(preset).every(key => {
                    const presetValues = [...(preset[key] || [])].sort();
                    const currentValues = [...(newFilters[key] || [])].sort();
                    return presetValues.length === currentValues.length &&
                        presetValues.every((val, idx) => val === currentValues[idx]);
                });

                if (isMatching) {
                    matchedPreset = presetName;
                    break;
                }
            }
        }

        // If filters don't match any preset, switch to Custom
        if (!matchedPreset && activeFilter !== 'Custom') {
            setActiveFilter('Custom');
            setFilterPresets(null);
        }
    }, [activeFilter, getFilterPreset]);


    console.log(myDashboardData, "API response Dashboard ")

    // PIE CHART DATA (LEGEND USES SAME DATA)
    // -------------------------------------------------------------

    // Color mapping for category names
    const getCategoryColor = (categoryName) => {
        const colorMap = {
            "Active Nutrition": "#0255D6",
            "Functional Beverage": "#8CC63F"
        };
        return colorMap[categoryName] || "#000000";
    };

    // Color mapping for sub-brand names
    const getSubBrandColor = (subBrandName) => {
        const colorMap = {
            "C4 Powder": "#C2CF2D",
            "Bloom Pop": "#59A671",
            "Bloom Sparkling Energy": "#C393E4",
            "XTEND": "#C5C5C5",
            "Cellucor": "#747474",
            "C4 Ultimate": "#1A1A1A",
            "C4 Smart Energy": "#1BA4E6",
            "C4 Performance": "#ECD01C",
            "C4": "#C2CF2D" // Default to C4 Powder color if just "C4"

        };
        return colorMap[subBrandName] || "#000000";
    };

    // Transform API data to pie chart format
    const pieCategory = useMemo(() => {
        const items = myDashboardData?.grossProfitByCategory?.items;
        if (!items || !Array.isArray(items) || items.length === 0) {
            return [];
        }
        return items.map(item => ({
            label: item.categoryName || "",
            value: item.percentage || 0,
            color: getCategoryColor(item.categoryName)
        }));
    }, [myDashboardData]);

    // Transform API data to pie chart format for sub-brands (Gross Profit by Sub Brand)
    const pieSubBrands = useMemo(() => {
        const items = myDashboardData?.grossProfitBySubBrand?.items;
        if (!items || !Array.isArray(items) || items.length === 0) return [];
        return items.map(item => {
            const name = item.subBrandSubcategory || "";
            return {
                label: name,
                value: item.percentage || 0,
                color: getSubBrandColor(name)
            };
        });
    }, [myDashboardData]);



    const getDistributorColor = (name) => {
        const colorMap = {
            "Other": "#000000",
            "Brown Bottling Group": "#3948AB",
            "The Lewis Bear Company": "#F4511E",
            "Great Basin Beverage": "#C52161",
            "Watkins Distributing": "#00897B",
            "The American Bottling Company (KDP)": "#7B21A3",
        };

        return colorMap[name] || "#000000";
    };
    const pieDistributorsData = useMemo(() => {
        const items = myDashboardData?.profitabilityByDistributorByFinancial?.items;
        if (!items || !Array.isArray(items) || items.length === 0) {
            return [];
        }
        return items.map(item => ({
            label: item.name || "",
            value: Number(item.percentage?.toFixed(1)) || 0,
            color: getDistributorColor(item.name)
        }));

    }, [myDashboardData]);

    console.log(pieDistributorsData, "distributor data")

    const getRetailerColor = (retailerName) => {
        if (!retailerName) return "#000000";

        const normalized = retailerName.trim();

        const colorMap = {
            "Other": "#000000",
            "HEB": "#7B21A3",
            "Circle K": "#C52161",
            "Kroger": "#8CC63F",
            "Walmart": "#1E63DA",
            "7-Eleven": "#E3A403",
        };

        return colorMap[normalized] || "#000000";
    };

    const pieRetailersData = useMemo(() => {
        const items = myDashboardData?.profitabilityByRetailerByFinancial?.items;
        if (!items || !Array.isArray(items) || items.length === 0) {
            return [];
        }
        return items.map(item => ({
            label: item.name || "",
            value: Number(item.percentage?.toFixed(1)) || 0,
            color: getRetailerColor(item.name)
        }));

    }, [myDashboardData]);

    console.log(pieRetailersData, "#####$$$$$$retailers")

    const getSubBrandchartColor = (subBrandName) => {
        const colorMap = {
            "C4 Powder": "#C2CF2D",
            "C4 Performance": "#ECD01C",
            "C4 Smart Energy": "#1BA4E6",
            "C4 Ultimate": "#1A1A1A",
            "Cellucor": "#747474",
            "XTEND": "#C5C5C5",
            "Bloom Sparkling Energy": "#C393E4",
            "Bloom Pop": "#59A671",
            "C4": "#C2CF2D",
            "Bloom": "#59A671"
        };

        return colorMap[subBrandName] || "#000000";
    };

    const convertSubBrandDataDynamic = useMemo(() => {
        const apiItems =
            myDashboardData?.netSalesBySubBrandMonthlyGrouped?.items || [];

        if (!Array.isArray(apiItems) || apiItems.length === 0) return null;

        // Sort Months Correctly
        const sortedItems = [...apiItems].sort(
            (a, b) => new Date("01-" + a.month) - new Date("01-" + b.month)
        );

        // X Labels Dynamically
        const xLabels = sortedItems.map((item) =>
            item.month.replace("-", ". ")
        );

        // Step 1: Collect ALL Unique Brand Names Dynamically
        const brandSet = new Set();

        sortedItems.forEach((monthItem) => {
            monthItem.values.forEach((v) => {
                let brand =
                    v.subBrandName === "C4" ? "C4 Powder" : v.subBrandName;

                brandSet.add(brand);
            });
        });

        // Convert Set → Array
        const labels = Array.from(brandSet);

        // Step 2: Build Series Dynamically
        const series = labels.map((label) => ({
            label,
            color: getSubBrandchartColor(label)
        }));

        // Step 3: Build DataMatrix Dynamically
        const dataMatrix = sortedItems.map((monthItem) => {
            const valueMap = {};

            monthItem.values.forEach((v) => {
                let brand =
                    v.subBrandName === "C4" ? "C4 Powder" : v.subBrandName;

                valueMap[brand] = v.netSalesAmount / 1_000_000;
            });

            // Row must follow labels order
            return labels.map((label) =>
                +(valueMap[label] || 0).toFixed(2)
            );
        });
        // Calculate stacked totals per month
        const stackedTotals = dataMatrix.map(row =>
            row.reduce((sum, val) => sum + val, 0)
        );

        // Get maximum stacked value
        const maxValue = Math.max(...stackedTotals);

        // Generate nice tick step
        function niceTickStep(maxVal, numTicks = 5) {
            const roughStep = maxVal / numTicks;
            const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
            const residual = roughStep / magnitude;

            let niceResidual;
            if (residual >= 5) niceResidual = 5;
            else if (residual >= 2) niceResidual = 2;
            else niceResidual = 1;

            return niceResidual * magnitude;
        }
        console.log(maxValue, "maxValue")
        const tickStep = niceTickStep(maxValue, 5);
        const maxTick = tickStep * Math.ceil(maxValue / tickStep);

        const yTicks = [];
        for (let tick = 0; tick <= maxTick; tick += tickStep) {
           yTicks.push(Number(tick.toFixed(4))); 
        }

        console.log(yTicks, "yTicks")

        // Final Output Structure
        return {
            title: "Net Sales by Sub Brand",
            xLabels,
            series,
            dataMatrix,
            yTicks
        };
    }, [myDashboardData]);



    console.log(convertSubBrandDataDynamic, "$$$$$$$$subbranddata")



    const convertCasesForecastLineChart = useMemo(() => {
        const apiItems =
            myDashboardData?.casesOrderedVsForecastedMonthly?.items || [];

        if (!Array.isArray(apiItems) || apiItems.length === 0) return null;

        // Sort Months Chronologically
        const sortedItems = [...apiItems].sort(
            (a, b) => new Date("01-" + a.month) - new Date("01-" + b.month)
        );

        // xLabels Format: "Apr. 2023"
        const xLabels = sortedItems.map((item) =>
            item.month.replace("-", ". ")
        );

        // Step 1: Collect Brand Names Dynamically
        const brandSet = new Set();

        sortedItems.forEach((monthItem) => {
            monthItem.values.forEach((v) => {
                let brand = v.name;

                // Normalize API names
                if (brand === "C4") brand = "C4 Powder";
                if (brand === "Bloom") brand = "Bloom Pop";

                brandSet.add(brand);
            });
        });

        // Convert Set → Array
        const labels = Array.from(brandSet);

        const series = labels.map((label) => ({
            label,
            color: getSubBrandchartColor(label)
        }));

        // Step 3: Build Actual + Forecast Matrices Dynamically
        const actualMatrix = [];
        const forecastMatrix = [];

        sortedItems.forEach((monthItem) => {
            const valueMap = {};

            // Store actual + forecast in lookup map
            monthItem.values.forEach((v) => {
                let brand = v.name;

                if (brand === "C4") brand = "C4 Powder";
                if (brand === "Bloom") brand = "Bloom Pop";

                valueMap[brand] = {
                    actual: v.actualCases / 1_000_000,
                    forecast: v.forecastCases / 1_000_000
                };
            });

            // Row must follow dynamic labels order
            const actualRow = labels.map(
                (label) => +(valueMap[label]?.actual || 0).toFixed(2)
            );

            const forecastRow = labels.map(
                (label) => +(valueMap[label]?.forecast || 0).toFixed(2)
            );

            actualMatrix.push(actualRow);
            forecastMatrix.push(forecastRow);
        });
        // Combine actual + forecast values
        const allValues = [
            ...actualMatrix.flat(),
            ...forecastMatrix.flat()
        ];

        const maxValue = Math.max(...allValues, 0);

        // Nice tick generator (handles decimals + large numbers)
        function generateNiceTicks(maxVal, tickCount = 5) {
            if (maxVal === 0) return [0];

            const roughStep = maxVal / tickCount;
            const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
            const normalized = roughStep / magnitude;

            let niceNormalized;

            if (normalized <= 1) niceNormalized = 1;
            else if (normalized <= 2) niceNormalized = 2;
            else if (normalized <= 5) niceNormalized = 5;
            else niceNormalized = 10;

            const step = niceNormalized * magnitude;
            const maxTick = Math.ceil(maxVal / step) * step;

            const ticks = [];
            for (let i = 0; i <= maxTick + step / 2; i += step) {
                ticks.push(Number(i.toFixed(6)));
            }

            return ticks;
        }

        const yTicks = generateNiceTicks(maxValue, 5);
        console.log(yTicks, "yTicks22")
        // -------------------------------------------------------
        return {
            title: "Cases Ordered vs Forecasted by Sub-Brand",
            xLabels,
            series,
            dataMatrix: {
                actual: actualMatrix,
                forecast: forecastMatrix
            },
            yTicks
        };
    }, [myDashboardData]);



    console.log(convertCasesForecastLineChart, "*******")

    const getNetGrossCashColor = (key) => {
        const colorMap = {
            net: "#62BDFF",
            gross: "#317FBB",
            cash: "#449C7D"
        };
        return colorMap[key] || "#000000";
    };

    const convertNetGrossCashChart = useMemo(() => {
        const apiItems =
            myDashboardData?.cashBalanceAndInventory?.items || [];

        if (!Array.isArray(apiItems)) return null;

        // Sort months
        const sortedItems = [...apiItems].sort(
            (a, b) => new Date("01-" + a.monthYear) - new Date("01-" + b.monthYear)
        );

        // xLabels
        const xLabels = sortedItems.map((item) =>
            item.monthYear.replace("-", ". ")
        );

        // Fields you want from API
        const selectedKeys = ["net", "gross", "cash"];

        // Dynamic Series Generation
        const series = selectedKeys.map((key) => ({
            key,

            label:
                key === "net"
                    ? "Net Sales"
                    : key === "gross"
                        ? "Gross Profit"
                        : key === "cash"
                            ? "Cash Balance"
                            : key,

            // Extra Property (only for cash)
            dashed: key === "cash",

            // Color from function
            color: getNetGrossCashColor(key)
        }));


        // DataMatrix
        const dataMatrix = sortedItems.map((item) => [
            +(item.netSalesAmount / 1_000_000).toFixed(2),
            +(item.grossProfitAmount / 1_000_000).toFixed(2),
            +(item.cashBalance / 1_000_000).toFixed(2)
        ]);
        console.log(dataMatrix, "$$$$$datamatrix")
        // Flatten all values in dataMatrix to a single array
        const allValues = dataMatrix.flat();

        // Get the max value
        const maxValue = Math.max(...allValues, 0);

        // Function to generate nice ticks dynamically
        function generateNiceTicks(maxVal, tickCount = 5) {
            if (maxVal === 0) return [0];

            const roughStep = maxVal / tickCount;
            const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
            const normalized = roughStep / magnitude;

            let niceNormalized;

            if (normalized <= 1) niceNormalized = 1;
            else if (normalized <= 2) niceNormalized = 2;
            else if (normalized <= 5) niceNormalized = 5;
            else niceNormalized = 10;

            const step = niceNormalized * magnitude;
            const maxTick = Math.ceil(maxVal / step) * step;

            const ticks = [];
            for (let i = 0; i <= maxTick + step / 2; i += step) {
                ticks.push(Number(i.toFixed(6)));
            }

            return ticks;
        }

        const yTicks = generateNiceTicks(maxValue, 5);
        return {
            title: "Net Sales, Gross Profit, and Cash Balance",
            xLabels,
            series,
            dataMatrix,
            yTicks
        };
    }, [myDashboardData]);
    console.log(convertNetGrossCashChart, "^^^^^^^")


    const getInventoryColor = (label) => {
        const map = {
            cashBalance: "#56BC4C",
            stockAmount: "#8A2BE2",
            cashIn: "#56BC4C",
            cashOut: "#E74C3C"
        };
        return map[label] || "#000";
    };

    const convertInventoryChartData = useMemo(() => {
        const apiItems =
            myDashboardData?.cashBalanceAndInventory?.items || [];

        if (!Array.isArray(apiItems) || apiItems.length === 0) return null;

        // Sort Months
        const sortedItems = [...apiItems].sort(
            (a, b) => new Date("01-" + a.monthYear) - new Date("01-" + b.monthYear)
        );

        // Dynamic X Labels
        const xLabels = sortedItems.map((item) =>
            item.monthYear.replace("-", ". ")
        );

        // Fields you want from API (Dynamic Keys)
        const selectedKeys = ["cashBalance", "stockAmount", "cashIn", "cashOut"];

        // Dynamic Series Generation
        const series = selectedKeys.map((key) => ({
            key,
            label:
                key === "cashBalance"
                    ? "Cash Balance"
                    : key === "stockAmount"
                        ? "Stock Amount (Dollars)"
                        : key === "cashIn"
                            ? "Cash In"
                            : key === "cashOut"
                                ? "Cash Out"
                                : key,

            type: key === "cashBalance" || key === "stockAmount" ? "line" : "bar",

            color: getInventoryColor(key)
        }));

        // Dynamic DataMatrix (Same Order as Series)
        const dataMatrix = sortedItems.map((item) =>
            selectedKeys.map((key) =>
                +((item[key] || 0) / 1_000_000).toFixed(2)
            )
        );

        const allValues = dataMatrix.flat(); // flatten 2D array
        const maxValue = Math.max(...allValues, 0);

        function generateNiceTicks(maxVal, tickCount = 5) {
            if (maxVal === 0) return [0];

            const roughStep = maxVal / tickCount;
            const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
            const normalized = roughStep / magnitude;

            let niceNormalized;
            if (normalized <= 1) niceNormalized = 1;
            else if (normalized <= 2) niceNormalized = 2;
            else if (normalized <= 5) niceNormalized = 5;
            else niceNormalized = 10;

            const step = niceNormalized * magnitude;
            const maxTick = Math.ceil(maxVal / step) * step;

            const ticks = [];
            for (let i = 0; i <= maxTick + step / 2; i += step) {
                ticks.push(Number(i.toFixed(6)));
            }

            return ticks;
        }

        const yTicks = generateNiceTicks(maxValue, 5);

        return {
            title: "Cash Balance and Inventory Value",
            xLabels,
            series,
            dataMatrix,
            yTicks
        };
    }, [myDashboardData]);

    console.log(convertInventoryChartData, "$$$$$$$$$")

    const convertRevenueToEBITChart = useMemo(() => {
        const apiData = myDashboardData?.revenueToEBIT;
        if (!apiData?.item) return null;

        const item = apiData.item;
        const period = "cy"; // use CY fields consistently

        const order = [
            "GrossSales",
            "TradeSpend",
            "NetSales",
            "cogs",
            "GrossProfit",
            "OperatingExpense",
            "ebit",
        ];

        const raw = order.map((field) => {
            const key = `${period}${field}`;
            const rawValue = item[key] ?? 0;

            let label = field.replace(/([A-Z])/g, " $1").trim();
            if (label.toLowerCase() === "cogs") label = "COGS";
            if (label.toLowerCase() === "ebit") label = "EBIT";

            const isNegativeBucket =
                label.includes("Spend") ||
                label.includes("Expense") ||
                label === "COGS";

            let valueInMillions = rawValue / 1_000_000;

            // Only flip sign for expense buckets that come as positive numbers
            if (isNegativeBucket && valueInMillions > 0) {
                valueInMillions = -valueInMillions;
            }

            const finalValue = Number(valueInMillions.toFixed(1));

            return {
                label,
                value: finalValue,
            };
        });
        let running = 0;
        const cumulativeValues = raw.map((r) => {
            running += r.value;
            return running;
        });

        const maxVal = Math.max(...cumulativeValues, 0);
        const minVal = Math.min(...cumulativeValues, 0);

        function generateNiceTicks(minVal, maxVal, tickCount = 5) {
            if (maxVal === minVal) return [0];

            const range = maxVal - minVal;
            const roughStep = range / tickCount;
            const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
            const normalized = roughStep / magnitude;

            let niceNormalized;
            if (normalized <= 1) niceNormalized = 1;
            else if (normalized <= 2) niceNormalized = 2;
            else if (normalized <= 5) niceNormalized = 5;
            else niceNormalized = 10;

            const step = niceNormalized * magnitude;

            const niceMin = Math.floor(minVal / step) * step;
            const niceMax = Math.ceil(maxVal / step) * step;

            const ticks = [];
            for (let i = niceMin; i <= niceMax + step / 2; i += step) {
                ticks.push(Number(i.toFixed(2)));
            }

            return ticks;
        }

        const yTicks = generateNiceTicks(minVal, maxVal, 5);
        console.log(yTicks, "waterfall chart")
        return {
            title: "Revenue to EBIT",
            raw,
            yTicks
        };
    }, [myDashboardData]);
    console.log(convertRevenueToEBITChart, "$$$$$$@@@@@@@waterfall")


    return (


        <Box sx={{
            ml: 0,
            transition: "margin-left 0.25s ease",
            "& .MuiBox-root": { backgroundImage: "none" }
        }} style={{
            // backgroundColor: '#f8f9fa',

            padding: isMobile ? '8px' : '0px 16px 0px 16px',
            // paddingBottom: '100px',
            fontFamily: 'inter !important'
        }} >
            {/* Loading Backdrop */}
            <Backdrop
                open={loading}
                sx={{
                    position: 'fixed',
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                }}
            >
                <CircularProgress color="primary" />
            </Backdrop>

            <Box
                sx={{
                    height: {
                        xs: "calc(100dvh - 140px)", // mobile header
                        md: "calc(100vh - 64px)",  // desktop header
                    },

                    overflowY: "auto",
                    // px: isMobile ? 1 : 2,
                    pb: "100px",
                    /* smooth scrolling */
                    scrollBehavior: "smooth",
                    WebkitOverflowScrolling: "touch", // iOS momentum
                    // backgroundColor: "green",
                    /* hide scrollbar */
                    scrollbarWidth: "none",          // Firefox
                    msOverflowStyle: "none",         // IE / Edge
                    "&::-webkit-scrollbar": {
                        display: "none",               // Chrome / Safari
                    },
                }}
            >




                {/* Section Title: KPI's */}
                <Typography sx={{ mb: 1, mt: 3 }}>
                    <span style={{ color: '#1672B980', fontWeight: 200, fontSize: '16px', }}>My Dashboard / </span><span style={{ fontWeight: 200, fontSize: '16px', }}>KPI's</span>
                </Typography>
                <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
                <KpiMetricscards cardsData={Kpiscardsdata} />

                {/* Section Title: Time Series */}
                <Typography sx={{ mb: 1, mt: 3 }}>
                    <span style={{ color: '#1672B980', fontWeight: 200, fontSize: '16px' }}>My Dashboard / </span><span style={{ fontWeight: 200, fontSize: '16px' }}>Time Series</span>
                </Typography>
                <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
                <TimeSeriescards timeSeriesCards={timeSeriesCardsdata} />

                {/* Section Title: Highlights with YTD/QTD/MTD buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, mt: 3 }}>
                    <Typography sx={{ fontSize: '16px' }}>
                        <span style={{ color: '#1672B980', fontWeight: 200, fontSize: '16px', }}>My Dashboard / </span> <span style={{ fontWeight: 200, fontSize: '16px', }}>Highlights</span>
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {['YTD', 'QTD', 'MTD'].map((label, idx) => (
                            <FilterChip
                                key={label}
                                label={label}
                                idx={idx}
                                activeIdx={highlightsActiveIdx}
                                onClick={handleHighlightsChipClick}
                            />


                        ))}
                    </Box>

                </Box>
                <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
                <HighlightsCards data={highlightCards} />
                <Box sx={{ mt: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography sx={{ fontSize: '16px' }}>
                            <span style={{ color: "#1672B980", fontWeight: 200, fontSize: '16px' }}>My Dashboard / </span> <span style={{ fontWeight: 200, fontSize: '16px' }}>Visualizations</span>
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1 }} className='visualization-filter-chips'>
                            {["YTD", "QTD", "MTD"].map((label, idx) => (
                                <FilterChip
                                    key={label}
                                    label={label}
                                    idx={idx}
                                    activeIdx={visualizationActiveIdx}
                                    onClick={handleVisualizationChipClick}

                                />
                            ))}
                        </Box>
                    </Box>

                    <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
                </Box>
                {/* Section Title: Visualizations */}

                <Grid
                    container
                    rowSpacing={2}
                    columnSpacing={2}
                    sx={{
                        mt: "26px !important",
                        alignItems: "stretch"   // keeps all cards same height
                    }}
                >
                    <Grid item xs={12} sm={6} md={6} lg={3} xl={3} sx={{
                        pt: { xs: "0px !important", sm: "0px !important", md: "0px !important" }
                    }} >
                        <PieLegendCard title="Gross Profit by Category" data={pieCategory} />
                    </Grid>

                    <Grid item xs={12} sm={6} md={6} lg={3} xl={3} sx={{
                        pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" }
                    }} >
                        <PieLegendCard title="Gross Profit by Sub Brand" data={pieSubBrands} />
                    </Grid>

                    <Grid item xs={12} sm={6} md={6} lg={3} xl={3} sx={{
                        pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" }
                    }} >
                        <PieLegendCard title="Top 5 Distributors by Profitability" data={pieDistributorsData} />
                    </Grid>

                    <Grid item xs={12} sm={6} md={6} lg={3} xl={3} sx={{
                        pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" }
                    }}>
                        <PieLegendCard title="Top 5 Retailers by Profitability" data={pieRetailersData} />
                    </Grid>
                </Grid>
                <Grid container sx={{ mt: "30px !important" }}>
                    <Grid item xs={12}>
                        {convertSubBrandDataDynamic?.xLabels?.length > 0 &&
                            convertSubBrandDataDynamic?.series?.length > 0 &&
                            convertSubBrandDataDynamic?.dataMatrix?.length > 0 && (

                                <Barcharts
                                    title={convertSubBrandDataDynamic.title}
                                    xLabels={convertSubBrandDataDynamic.xLabels}
                                    series={convertSubBrandDataDynamic.series}
                                    dataMatrix={convertSubBrandDataDynamic.dataMatrix}
                                    yTicks={convertSubBrandDataDynamic.yTicks}
                                    yAxisLabel="Net Sales ($M)"
                                    rounded={true}
                                />

                            )}

                    </Grid>
                </Grid>
                <Grid container sx={{ mt: "30px !important" }}>
                    <Grid item xs={12}>
                        {convertCasesForecastLineChart?.xLabels?.length > 0 &&
                            convertCasesForecastLineChart?.series?.length > 0 &&
                            convertCasesForecastLineChart?.dataMatrix?.actual?.length > 0 &&
                            convertCasesForecastLineChart?.dataMatrix?.forecast?.length > 0 && (
                                <LineChartSubBrand
                                    title={convertCasesForecastLineChart.title}
                                    xLabels={convertCasesForecastLineChart.xLabels}
                                    series={convertCasesForecastLineChart.series}
                                    dataMatrix={convertCasesForecastLineChart.dataMatrix}
                                    yTicks={convertCasesForecastLineChart.yTicks}
                                />
                            )}

                    </Grid>
                </Grid>
                <Grid container sx={{ mt: "30px !important" }}>
                    <Grid item xs={12}>
                        {convertNetGrossCashChart?.xLabels?.length > 0 &&
                            convertNetGrossCashChart?.series?.length > 0 &&
                            convertNetGrossCashChart?.dataMatrix?.length > 0 && (
                                <LineChartsale
                                    title={convertNetGrossCashChart.title}
                                    xLabels={convertNetGrossCashChart.xLabels}
                                    series={convertNetGrossCashChart.series}
                                    dataMatrix={convertNetGrossCashChart.dataMatrix}
                                    yTicks={convertNetGrossCashChart.yTicks}
                                    fillColor="#62BBFF"
                                    yAxisLabel="Dollars ($M)"
                                />
                            )}


                    </Grid>
                </Grid>
                <Grid container sx={{ mt: "30px !important" }}>
                    <Grid item xs={12}>
                        {convertInventoryChartData?.title &&
                            convertInventoryChartData?.xLabels?.length > 0 &&
                            convertInventoryChartData?.series?.length > 0 &&
                            convertInventoryChartData?.dataMatrix?.length > 0 && (
                                <InventoryCharts
                                    title={convertInventoryChartData.title}
                                    xLabels={convertInventoryChartData.xLabels}
                                    series={convertInventoryChartData.series}
                                    dataMatrix={convertInventoryChartData.dataMatrix}
                                    yTicks={convertInventoryChartData.yTicks}
                                />
                            )}

                    </Grid>
                </Grid>
                <Grid container sx={{ mt: "30px !important" }}>
                    <Grid item xs={12}>
                        {convertRevenueToEBITChart &&
                            convertRevenueToEBITChart.raw &&
                            convertRevenueToEBITChart.raw.length > 0 && (
                                <WaterfallChart
                                    title={convertRevenueToEBITChart.title}
                                    rawData={convertRevenueToEBITChart.raw}
                                    yTicks={convertRevenueToEBITChart.yTicks}
                                />
                            )}

                    </Grid>
                </Grid>

            </Box>




        </Box>




    );
};

export default Dashboard;